import {
  forwardRef,
  useImperativeHandle,
  useState,
  type ReactNode,
} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import IosShareIcon from '@mui/icons-material/IosShare'
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import { useTranslation } from 'react-i18next'
import { useStandaloneMode } from '../hooks/useStandaloneMode'
import { useInstallPrompt } from '../hooks/useInstallPrompt'

/** Imperative handle exposed by `<InstallAppButton ref={...}>`. */
export interface InstallAppButtonHandle {
  /** Triggers the right install path for the current platform:
   *   - already in standalone mode → no-op
   *   - iOS Safari (or iPadOS) → opens the manual-instructions dialog
   *   - Android with captured `beforeinstallprompt` → fires the native prompt
   *   - anything else → snackbar telling user to open in Chrome/Safari */
  trigger: () => void
  /** Whether the install affordance is meaningful in the current context.
   *  False when already running standalone — UIs should hide their trigger. */
  available: boolean
}

interface InstallAppButtonProps {
  /** Optional inline render-prop trigger. Mostly used by the desktop icon
   *  rail; the mobile MobileSheet uses the imperative `ref` API instead so
   *  the dialog/snackbar can be mounted OUTSIDE the sheet's unmount scope. */
  children?: (args: { onClick: () => void; available: boolean }) => ReactNode
}

/**
 * Manages the install-lifecycle dialog and snackbar. Mounted OUTSIDE any
 * unmounting context (modal, drawer, sheet) so the dialog actually appears
 * when triggered.
 *
 * Two trigger styles:
 *
 *   1. Imperative — `<InstallAppButton ref={r} />` + `r.current?.trigger()`.
 *      Use this when the trigger UI lives inside a SwipeableDrawer or any
 *      other element that unmounts on close. The drawer's child can call
 *      `trigger()` and the dialog will still render because the dialog's
 *      state lives in this stable mount.
 *
 *   2. Render-prop — `<InstallAppButton>{({ onClick }) => <Btn .../>}</>`.
 *      Use this when the trigger UI lives in a stable parent (desktop
 *      sidebar, settings page) that doesn't unmount.
 */
export const InstallAppButton = forwardRef<InstallAppButtonHandle, InstallAppButtonProps>(
  function InstallAppButton({ children }, ref) {
    const { t } = useTranslation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { isStandalone } = useStandaloneMode()
    const { canInstall, isIos, promptInstall } = useInstallPrompt()
    const [iosDialogOpen, setIosDialogOpen] = useState(false)
    const [snack, setSnack] = useState<string | null>(null)

    const available = !isStandalone

    const handleClick = () => {
      if (isStandalone) return
      if (isIos) {
        setIosDialogOpen(true)
        return
      }
      if (canInstall) {
        void promptInstall()
        return
      }
      setSnack(t('install.notAvailable'))
    }

    useImperativeHandle(ref, () => ({ trigger: handleClick, available }), [
      handleClick,
      available,
    ])

    return (
      <>
        {children?.({ onClick: handleClick, available })}

        <Dialog
          open={iosDialogOpen}
          onClose={() => setIosDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('install.iosTitle')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('install.iosBody')}
            </DialogContentText>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'primary.main',
                  }}
                >
                  <IosShareIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t('install.iosStep1Title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('install.iosStep1Body')}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'primary.main',
                  }}
                >
                  <AddBoxOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t('install.iosStep2Title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('install.iosStep2Body')}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIosDialogOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar anchored TOP on mobile so it isn't hidden behind the
            fixed BottomNav. On desktop bottom-center is fine. */}
        <Snackbar
          open={snack !== null}
          autoHideDuration={5000}
          onClose={() => setSnack(null)}
          anchorOrigin={
            isMobile
              ? { vertical: 'top', horizontal: 'center' }
              : { vertical: 'bottom', horizontal: 'center' }
          }
          sx={isMobile ? { mt: 'calc(56px + var(--safe-top, 0px) + 8px)' } : undefined}
        >
          <Alert severity="info" onClose={() => setSnack(null)} variant="filled">
            {snack}
          </Alert>
        </Snackbar>
      </>
    )
  },
)
